import React from "react";
import { motion } from "framer-motion";
import {
  ChatCircleDots,
  Plug,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";

export default function StepPathChoice({ onChoose }) {
  return (
    <div data-testid="step-path-choice" className="max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
            Final step — your choice
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight">
          What's your next move?
        </h2>
        <p className="mt-3 text-slate-600 text-lg max-w-2xl mx-auto">
          Two paths from here — talk to a specialist for the full assessment, or grab
          backup power right now from our Shopify store.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <motion.button
          type="button"
          onClick={() => onChoose("assessment")}
          data-testid="path-assessment"
          whileHover={{ y: -4 }}
          className="text-left p-8 rounded-3xl border-2 border-slate-200 hover:border-sky-500 hover:shadow-2xl bg-white transition-all flex flex-col"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-5">
            <ChatCircleDots size={28} weight="duotone" className="text-white" />
          </div>
          <h3 className="font-display text-2xl font-semibold text-slate-900">
            Free assessment & quote
          </h3>
          <p className="text-slate-600 mt-2 flex-1 leading-relaxed">
            A Maui Starpower specialist reviews your bill, calls within 24 hours, and
            walks you through solar, financing, HVAC, or whatever fits.
          </p>
          <div className="space-y-1.5 mt-5 text-sm text-slate-700">
            <Bullet>Full custom solar+battery quote</Bullet>
            <Bullet>Financing options (Unlock, conventional, HVAC partners)</Bullet>
            <Bullet>35% Tax Equity Offset waitlist spot</Bullet>
            <Bullet>No commitment, no pressure</Bullet>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-sky-600 font-semibold text-sm">
            Talk to a specialist <ArrowRight size={16} weight="bold" />
          </div>
        </motion.button>

        <motion.button
          type="button"
          onClick={() => onChoose("ecoflow")}
          data-testid="path-ecoflow"
          whileHover={{ y: -4 }}
          className="text-left p-8 rounded-3xl border-2 border-slate-200 hover:border-emerald-500 hover:shadow-2xl bg-white transition-all flex flex-col relative"
        >
          <span className="absolute top-5 right-5 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-500 px-2.5 py-1 rounded-full">
            Ship today
          </span>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-5">
            <Plug size={28} weight="duotone" className="text-white" />
          </div>
          <h3 className="font-display text-2xl font-semibold text-slate-900">
            Get backup power now
          </h3>
          <p className="text-slate-600 mt-2 flex-1 leading-relaxed">
            We're EcoFlow certified installers. Tell us how much backup you need —
            we'll match you to the right portable power station from our Shopify store.
          </p>
          <div className="space-y-1.5 mt-5 text-sm text-slate-700">
            <Bullet>From DELTA 3 (small) to OCEAN Pro (full home)</Bullet>
            <Bullet>Sized to your bill in 30 seconds</Bullet>
            <Bullet>Direct to shop.starpower.network</Bullet>
            <Bullet>Outage-ready before the next storm</Bullet>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm">
            See my matched product <ArrowRight size={16} weight="bold" />
          </div>
        </motion.button>
      </div>

      <p className="text-center text-xs text-slate-500 mt-8">
        You can always do both — pick a portable now and book an assessment for the long-term plan.
      </p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle size={16} weight="fill" className="text-emerald-500 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
